import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingHeader from '../components/MarketingHeader';
import '../styles/MarketingTheme.css';

/*spaces*/

function AboutPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="marketing-page stunning-theme">
            <MarketingHeader />

            {/* Hero Section */}
            <section className="marketing-section marketing-section-hero">
                <div className="marketing-container text-center">
                    <div className="marketing-section-badge">About RedSorm</div>
                    <h1 className="marketing-hero-title">
                        Restaurant dining & <br />
                        <span className="marketing-hero-gradient">Smart order management</span>
                    </h1>
                    <p className="marketing-hero-description mx-auto">
                        RedSorm is revolutionizing the restaurant industry with our comprehensive QR-based ordering and management platform. We empower restaurants to deliver exceptional dining experiences while streamlining operations.
                    </p>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="marketing-section marketing-section-dark">
                <div className="marketing-container">
                    <div className="marketing-hero-content">
                        <div className="marketing-card">
                            <div className="marketing-card-icon" style={{ background: 'var(--gradient-primary)', color: 'white' }}>
                                <i className="fas fa-bullseye"></i>
                            </div>
                            <h2 className="marketing-card-title">Our Mission</h2>
                            <p className="marketing-card-description">
                                To transform traditional dining into seamless, technology-driven experiences. We're committed to helping restaurants increase efficiency, reduce costs, and delight customers with innovative solutions.
                            </p>
                        </div>
                        <div className="marketing-card">
                            <div className="marketing-card-icon" style={{ background: 'var(--gradient-secondary)', color: 'white' }}>
                                <i className="fas fa-eye"></i>
                            </div>
                            <h2 className="marketing-card-title">Our Vision</h2>
                            <p className="marketing-card-description">
                                To become the world's leading restaurant management platform, powering millions of dining experiences globally. We envision a future where every restaurant leverages technology to maximize potential.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="marketing-section">
                <div className="marketing-container marketing-container-narrow">
                    <h2 className="marketing-section-title text-center">Our Story</h2>
                    <div className="marketing-card mt-md">
                        <p className="marketing-card-description mb-sm">
                            RedSorm was born from a simple observation: restaurants were struggling with outdated ordering systems, inefficient kitchen workflows, and limited customer insights. In 2023, our founders—experienced technologists and restaurant professionals—came together to create a solution.
                        </p>
                        <p className="marketing-card-description mb-sm">
                            What started as a QR code ordering system quickly evolved into a comprehensive restaurant operating system. Today, RedSorm powers everything from customer ordering to kitchen management, analytics, inventory control, and staff coordination.
                        </p>
                        <p className="marketing-card-description">
                            We've helped hundreds of restaurants increase their revenue by an average of 25%, reduce order processing time by 40%, and deliver superior customer experiences. Our journey is just beginning, and we're excited to continue innovating for the restaurant industry.
                        </p>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="marketing-section marketing-section-dark">
                <div className="marketing-container">
                    <div className="text-center mb-lg">
                        <h2 className="marketing-section-title">Our Core Values</h2>
                        <p className="marketing-section-subtitle mx-auto">The principles that guide everything we do.</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
                        {[
                            {
                                icon: 'fa-rocket',
                                title: 'Innovation',
                                description: 'We constantly push boundaries, leveraging cutting-edge technology to solve real problems for restaurants.'
                            },
                            {
                                icon: 'fa-heart',
                                title: 'Customer-Centric',
                                description: 'Every feature we build is designed with restaurant owners, staff, and diners in mind.'
                            },
                            {
                                icon: 'fa-shield-alt',
                                title: 'Reliability',
                                description: 'We ensure 99.9% uptime and enterprise-grade security to protect your business.'
                            },
                            {
                                icon: 'fa-users',
                                title: 'Partnership',
                                description: 'We see ourselves as partners in your success, providing ongoing support and guidance.'
                            },
                            {
                                icon: 'fa-chart-line',
                                title: 'Growth',
                                description: 'We are dedicated to helping restaurants scale and achieve sustainable growth.'
                            },
                            {
                                icon: 'fa-lightbulb',
                                title: 'Transparency',
                                description: 'We believe in honest communication, clear pricing, and open collaboration.'
                            }
                        ].map((value, index) => (
                            <div key={index} className="marketing-card text-center">
                                <div className="marketing-card-icon mx-auto" style={{ background: 'rgba(255,255,255,0.05)' }}>
                                    <i className={`fas ${value.icon}`} style={{ color: 'var(--brand-primary)' }}></i>
                                </div>
                                <h3 className="marketing-card-title text-xl">{value.title}</h3>
                                <p className="marketing-card-description">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="marketing-section">
                <div className="marketing-container">
                    <div className="marketing-cta">
                        <h2 className="marketing-cta-title">Ready to Transform Your Restaurant?</h2>
                        <p className="marketing-cta-description">
                            Join hundreds of restaurants already using RedSorm to streamline operations and delight customers.
                        </p>
                        <div className="flex-center gap-md flex-wrap">
                            <button
                                onClick={() => navigate('/signup')}
                                className="marketing-btn marketing-btn-large bg-white text-purple-600 hover:bg-gray-100"
                            >
                                Start Free Trial
                            </button>
                            <button
                                onClick={() => navigate('/features')}
                                className="marketing-btn marketing-btn-large marketing-btn-secondary"
                            >
                                Explore Features
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="marketing-footer">
                <div className="marketing-footer-content">
                    <p>&copy; {new Date().getFullYear()} RedSorm. All rights reserved.</p>
                    <div className="marketing-footer-links">
                        <a href="/terms-of-service" className="marketing-footer-link">Terms of Service</a>
                        <a href="/privacy-policy" className="marketing-footer-link">Privacy Policy</a>
                        <a href="/cookie-policy" className="marketing-footer-link">Cookie Policy</a>
                    </div>
                </div>
            </footer>

        </div>
    );
}

export default AboutPage;
